/**
 * FeaturesMultiSelect / ExtrasMultiSelect / LocationsMultiSelect
 *
 * Three chip-based multi-select pickers used in the vehicle editor.
 * Consolidated here since they share layout/UX and the file stays lean.
 */

export function FeaturesMultiSelect({ all, selected, onChange }) {
  const selectedSet = new Set(selected || []);
  const catalogueNames = new Set(all.map((f) => f.name));
  const orphans = (selected || []).filter((s) => !catalogueNames.has(s));

  const toggle = (name) => {
    const next = new Set(selectedSet);
    if (next.has(name)) next.delete(name); else next.add(name);
    onChange(Array.from(next));
  };

  if (all.length === 0) {
    return (
      <div className="text-xs text-neutral-500 dark:text-neutral-400 py-2" data-testid="features-multiselect-empty">
        No features configured. Add some under <em>Rental Settings → Car Features</em>.
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="features-multiselect">
      <div className="flex flex-wrap gap-2">
        {all.map((f) => {
          const on = selectedSet.has(f.name);
          return (
            <button
              type="button"
              key={f.id}
              onClick={() => toggle(f.name)}
              data-testid={`feat-chip-${f.id}`}
              className={`text-xs px-3 py-1.5 rounded border transition-colors ${
                on ? "bg-black text-white border-black dark:bg-white dark:text-black dark:border-white"
                   : "border-black/15 dark:border-white/15 hover:border-black dark:hover:border-white text-neutral-700 dark:text-neutral-300"
              }`}
            >
              {on ? "✓ " : ""}{f.name}
            </button>
          );
        })}
      </div>
      {orphans.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-widest text-amber-600 dark:text-amber-400 mb-1">
            Legacy tags (not in catalogue)
          </p>
          <div className="flex flex-wrap gap-2">
            {orphans.map((name) => (
              <button
                type="button"
                key={name}
                onClick={() => toggle(name)}
                data-testid={`feat-orphan-${name}`}
                className="text-xs px-3 py-1.5 rounded border border-amber-300 dark:border-amber-800 bg-amber-50/50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300 hover:opacity-90"
                title="This tag no longer exists in the Features catalogue — click to remove."
              >
                ✕ {name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function ExtrasMultiSelect({ all, selected, onChange }) {
  const selectedSet = new Set(selected || []);
  const toggle = (id) => {
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange(Array.from(next));
  };
  const clear = () => onChange([]);

  if (all.length === 0) {
    return (
      <div className="text-xs text-neutral-500 dark:text-neutral-400 py-2" data-testid="extras-multiselect-empty">
        No extras configured. Add some under <em>Rental Settings → Extras</em>.
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid="extras-multiselect">
      <div className="flex flex-wrap gap-2">
        {all.map((x) => {
          const on = selectedSet.has(x.id);
          return (
            <button
              type="button"
              key={x.id}
              onClick={() => toggle(x.id)}
              data-testid={`extra-chip-${x.id}`}
              title={`€${Number(x.price).toFixed(2)} ${x.pricing_type === "per_day" ? "per day" : "per reservation"}`}
              className={`text-xs px-3 py-1.5 rounded border transition-colors ${
                on ? "bg-black text-white border-black dark:bg-white dark:text-black dark:border-white"
                   : "border-black/15 dark:border-white/15 hover:border-black dark:hover:border-white text-neutral-700 dark:text-neutral-300"
              }`}
            >
              {on ? "✓ " : ""}{x.name}
              <span className="ml-1 opacity-70">€{Number(x.price).toFixed(0)}/{x.pricing_type === "per_day" ? "day" : "res"}</span>
            </button>
          );
        })}
      </div>
      <div className="flex items-center justify-between text-[10px]">
        <span className="text-neutral-500 dark:text-neutral-400">
          {selectedSet.size === 0
            ? "Empty selection → ALL active extras are allowed for this group."
            : `${selectedSet.size} selected → only these extras appear in booking forms.`}
        </span>
        {selectedSet.size > 0 && (
          <button
            type="button"
            onClick={clear}
            className="text-neutral-500 dark:text-neutral-400 hover:text-black dark:hover:text-white underline"
            data-testid="extras-multiselect-clear"
          >
            Clear all
          </button>
        )}
      </div>
    </div>
  );
}

export function LocationsMultiSelect({ all, selected, onChange }) {
  const selectedSet = new Set(selected || []);
  const toggle = (name) => {
    const next = new Set(selectedSet);
    if (next.has(name)) next.delete(name); else next.add(name);
    onChange(Array.from(next));
  };
  const clear = () => onChange([]);

  if (!all || all.length === 0) {
    return (
      <p className="text-xs text-neutral-500 dark:text-neutral-400 py-2" data-testid="locations-multiselect-empty">
        No locations configured yet. Add some under <em>Admin → Locations</em>.
      </p>
    );
  }

  return (
    <div className="space-y-2" data-testid="locations-multiselect">
      <div className="flex flex-wrap gap-2">
        {all.map((l) => {
          const on = selectedSet.has(l.name);
          return (
            <button
              type="button"
              key={l.id}
              onClick={() => toggle(l.name)}
              data-testid={`loc-chip-${l.id}`}
              className={`text-xs px-3 py-1.5 rounded border transition-colors ${
                on ? "bg-black text-white border-black dark:bg-white dark:text-black dark:border-white"
                   : "border-black/15 dark:border-white/15 hover:border-black dark:hover:border-white text-neutral-700 dark:text-neutral-300"
              }`}
            >
              {on ? "✓ " : ""}{l.name}
            </button>
          );
        })}
      </div>
      <div className="flex items-center justify-between text-[10px]">
        <span className="text-neutral-500 dark:text-neutral-400">
          {selectedSet.size === 0
            ? "Empty selection → Car Group available at EVERY location."
            : `${selectedSet.size} selected → Car Group only offered at these locations.`}
        </span>
        {selectedSet.size > 0 && (
          <button
            type="button"
            onClick={clear}
            className="text-neutral-500 dark:text-neutral-400 hover:text-black dark:hover:text-white underline"
            data-testid="locations-multiselect-clear"
          >
            Clear all
          </button>
        )}
      </div>
    </div>
  );
}
